const MASKED_VALUE = 'SENSITIVE DATA'

// Project-specific rules (national id formats, local phone formats, backend paths) are
// read from env at module load so server and edge bundles see them without wiring;
// the browser logger only has the built-in rules:
//   LOG_SANITIZE_KEYS=fnr,pid,personnummer*   (comma-separated; trailing * = substring match)
//   LOG_SANITIZE_PATTERNS=<regex>;<regex>     (semicolon-separated, applied to every string;
//                                              anchor the left side to keep them linear)
//   LOG_SANITIZE_PATH_SEGMENTS=autocomplete   (comma-separated; masks the segment that follows)
const processEnv = typeof process !== 'undefined' ? process.env : undefined
const parseList = (value: string | undefined, separator = ','): string[] =>
  (value ?? '')
    .split(separator)
    .map((item) => item.trim())
    .filter(Boolean)
const normalizeKey = (key: string): string =>
  key.toLowerCase().replace(/[^a-z0-9]/g, '')
const projectKeys = parseList(processEnv?.LOG_SANITIZE_KEYS)
const projectKeyParts = projectKeys
  .filter((key) => key.endsWith('*'))
  .map(normalizeKey)
const projectExactKeys = projectKeys
  .filter((key) => !key.endsWith('*'))
  .map(normalizeKey)
const projectPatterns = parseList(
  processEnv?.LOG_SANITIZE_PATTERNS,
  ';'
).flatMap((source) => {
  try {
    return [new RegExp(source, 'g')]
  } catch {
    return []
  }
})
const projectPathSegments = parseList(processEnv?.LOG_SANITIZE_PATH_SEGMENTS)

const SECRET_KEY_PARTS = [
  'password',
  'authorization',
  'apikey',
  'clientsecret',
  'secret',
  'token',
]
const SECRET_KEYS = [
  'otp',
  'otpcode',
  'cookie',
  'cookies',
  'setcookie',
  'session',
]

const PII_KEY_PARTS = [
  'email',
  'phone',
  'mobile',
  'msisdn',
  'firstname',
  'lastname',
  'fullname',
  'displayname',
  'displayedname',
  'shortname',
  'givenname',
  'familyname',
  'surname',
  'username',
  'address',
  'street',
  'postalcode',
  'postcode',
  'zipcode',
  'birthdate',
  'dateofbirth',
  'birthday',
  'nationalid',
  'personalnumber',
  'latitude',
  'longitude',
  'cardnumber',
  'cardholder',
  'accountholder',
  'accountnumber',
  'iban',
  ...projectKeyParts,
]
const PII_KEYS = [
  'uid',
  'displayuid',
  'mail',
  'line1',
  'line2',
  'town',
  'city',
  'zip',
  'ssn',
  'dob',
  'ip',
  'clientip',
  'remoteip',
  'xforwardedfor',
  'xrealip',
  'gender',
  'lat',
  'lng',
  'lon',
  ...projectExactKeys,
]
// Keys that only reference or qualify personal data stay readable: addressId, emailVerified,
// addressType, isDefaultAddress, hasPrescriptions
const DERIVED_KEY_PREFIX_RE = /^(?:is|has)(?:[A-Z_-]|$)/
const DERIVED_KEY_SUFFIXES = [
  'id',
  'ids',
  'type',
  'verified',
  'exists',
  'count',
  'length',
  'required',
  'enabled',
]

// `name` is a person name only inside person-like objects (or lists of them) or next to
// keys that only person objects have; a store has a phone, but no first name or email
const PERSON_PARENT_KEYS = [
  'user',
  'customer',
  'profile',
  'principal',
  'userdata',
  'sapcustomer',
  'member',
  'contact',
  'reviewer',
  'author',
  'recipient',
]
const PERSON_SIBLING_PARTS = [
  'firstname',
  'lastname',
  'email',
  'dateofbirth',
  'birthdate',
  'customerid',
]
const PERSON_SIBLING_KEYS = [
  'mail',
  'uid',
  'displayuid',
  'mobile',
  'mobilenumber',
]

const PUBLIC_PATH_SEGMENTS = ['current', 'anonymous', 'me']
const escapeRegExp = (value: string): string =>
  value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
const PATH_SEGMENT_RE =
  projectPathSegments.length > 0
    ? new RegExp(
        `/(${projectPathSegments.map(escapeRegExp).join('|')})/(?!${MASKED_VALUE})([^/?#\\s\\u001b]+)`,
        'g'
      )
    : undefined
// Whole string is a form-encoded body: values run up to the next `&`
const FORM_BODY_RE = /^[^=&\s]+=[^&]*(?:&[^=&\s]+=[^&]*)*$/
// key=value pairs in query strings, cookies and free text
const KEY_VALUE_RE = new RegExp(
  `(^|[?&;\\s])([^=&;\\s]+)=(?!${MASKED_VALUE})([^&;\\s]*)`,
  'g'
)
// Raw HTTP messages (request/status line or CRLF-separated headers) get `Name: value` masking
const HTTP_MESSAGE_RE =
  /^(?:(?:GET|HEAD|POST|PUT|PATCH|DELETE|OPTIONS) \S+ HTTP\/|HTTP\/\d)|\r\n/
const HEADER_LINE_RE = new RegExp(
  `(^|[\\r\\n])([\\w-]+):([ \\t]*)(?!${MASKED_VALUE})([^\\r\\n]*)`,
  'g'
)
// Token values need a separator and must look like a token (16+ chars with a digit);
// Bearer/Basic credentials are always masked
const TOKEN_CHARS = '[\\w.~+/=-]'
const TOKEN_VALUE_RE = new RegExp(
  `\\b(?:((?:access|refresh|id)[ _-]?token\\s*[:=]\\s*)(?!${MASKED_VALUE})(?=${TOKEN_CHARS}*\\d)${TOKEN_CHARS}{16,}|((?:bearer|basic)\\s+)(?!${MASKED_VALUE})${TOKEN_CHARS}{16,})`,
  'gi'
)
// JWS (3 parts) and JWE (5 parts, second may be empty)
const JWT_RE = /\beyJ[\w-]{5,}(?:\.[\w-]*){2,4}/g
// Left guard keeps the scan linear on long strings; `@2x.` excludes retina asset names
const EMAIL_RE =
  /(?<![A-Za-z0-9._%+-])[A-Za-z0-9._%+-]+(?:@|%40)(?![1-4]x\.)[A-Za-z0-9.-]+\.[A-Za-z]{2,}/gi
// International phone numbers (+<country code> then 6-12 digits with optional separators);
// national formats and identity numbers are project rules (LOG_SANITIZE_PATTERNS)
const PHONE_INTL_RE = /(?<![\w+])\+\d{1,3}(?:[ -]?\d){6,12}(?!\d)/g
// Integers that JSON.parse would round are quoted first
const BIG_INT_RE = /(?<=[:[,]\s*)-?\d{16,}(?=\s*[,\]}])/g
// eslint-disable-next-line no-control-regex
const ANSI_RE = /\u001b\[[0-9;]*m/g
const MAX_DEPTH = 64

const SERIALIZED_ERROR = Symbol.for('maestro.logger.serializedError')

export interface SerializedError {
  name: string
  message: string
  stack?: string
  [key: string]: unknown
}

interface Context {
  seen: WeakSet<object>
  stripAnsi: boolean
  parentKey: string
  depth: number
}

const createContext = (stripAnsi: boolean): Context => ({
  seen: new WeakSet<object>(),
  stripAnsi,
  parentKey: '',
  depth: 0,
})

const descend = (ctx: Context, parentKey: string): Context => ({
  ...ctx,
  parentKey,
  depth: ctx.depth + 1,
})

export function sanitizeLogs(args: unknown[]): unknown[] {
  return args.map((arg) => toSanitizedValue(arg, createContext(true)))
}

/** Pattern masking only (keeps ANSI colours), for log messages and URLs */
export function sanitizeString(str: string): string {
  return sanitizeStringValue(str, createContext(false))
}

/**
 * Error objects are converted to plain objects by the sanitizer (own fields such as
 * `status`, `code`, `data` are kept and sanitized); this marks them so JSON formatters
 * can still place them in the top-level `error` field.
 */
export function isSerializedError(value: unknown): value is SerializedError {
  return (
    typeof value === 'object' &&
    value !== null &&
    (value as Record<symbol, unknown>)[SERIALIZED_ERROR] === true
  )
}

function isSensitiveKey(rawKey: string): boolean {
  const key = normalizeKey(rawKey)
  if (SECRET_KEYS.includes(key) || PII_KEYS.includes(key)) {
    return true
  }
  if (
    DERIVED_KEY_PREFIX_RE.test(rawKey) ||
    DERIVED_KEY_SUFFIXES.some((suffix) => key.endsWith(suffix))
  ) {
    return false
  }
  return [...SECRET_KEY_PARTS, ...PII_KEY_PARTS].some((part) =>
    key.includes(part)
  )
}

const isPersonParent = (key: string): boolean =>
  PERSON_PARENT_KEYS.includes(key) ||
  PERSON_PARENT_KEYS.includes(key.replace(/s$/, ''))

const isPersonSibling = (key: string): boolean =>
  PERSON_SIBLING_KEYS.includes(key) ||
  PERSON_SIBLING_PARTS.some((part) => key.includes(part))

const isPersonName = (
  key: string,
  parentKey: string,
  siblingKeys: string[]
): boolean =>
  key === 'name' &&
  (isPersonParent(parentKey) || siblingKeys.some(isPersonSibling))

const maskPair = (key: string, masked: string, original: string): string =>
  isSensitiveKey(key) ? masked : original

/**
 * Recursively sanitizes log arguments:
 * - keys that denote secrets or personal data are replaced with "SENSITIVE DATA"
 * - strings (values and keys) are scanned for emails, phone numbers, tokens,
 *   sensitive query/form/cookie/header fields and project patterns; JSON strings
 *   are parsed and sanitized
 * - Error instances become plain objects (name, message, stack, own fields)
 *
 * Uses WeakSet to detect and handle circular references
 */
function toSanitizedValue(arg: unknown, ctx: Context): unknown {
  if (typeof arg === 'string') {
    return sanitizeStringValue(arg, ctx)
  }
  if (typeof arg === 'bigint') {
    return arg.toString()
  }
  if (typeof arg !== 'object' || arg === null) {
    return arg
  }
  if (ctx.seen.has(arg)) {
    return '[Circular]'
  }
  if (ctx.depth > MAX_DEPTH) {
    return '[Truncated]'
  }
  ctx.seen.add(arg)
  try {
    if (arg instanceof Error || isSerializedError(arg)) {
      const result = sanitizeObject(
        arg instanceof Error ? errorToObject(arg) : arg,
        ctx,
        true
      )
      Object.defineProperty(result, SERIALIZED_ERROR, { value: true })
      return result
    }
    if (arg instanceof Map) {
      return sanitizeObject(Object.fromEntries(arg), ctx)
    }
    if (arg instanceof Set) {
      return toSanitizedValue([...arg], ctx)
    }
    if (ArrayBuffer.isView(arg)) {
      return `[${arg.constructor.name}: ${arg.byteLength} bytes]`
    }
    // Date, URL and similar: serialize the same way JSON.stringify would, then sanitize
    if (typeof (arg as { toJSON?: unknown }).toJSON === 'function') {
      const json = (arg as { toJSON: () => unknown }).toJSON()
      return toSanitizedValue(json, descend(ctx, ctx.parentKey))
    }
    if (Array.isArray(arg)) {
      const itemCtx = descend(ctx, ctx.parentKey)
      return arg.map((item) => toSanitizedValue(item, itemCtx))
    }
    return sanitizeObject(arg as Record<string, unknown>, ctx)
  } finally {
    ctx.seen.delete(arg)
  }
}

function sanitizeObject(
  obj: Record<string, unknown>,
  ctx: Context,
  isError = false
): Record<string, unknown> {
  const entries = Object.entries(obj)
  const keys = entries.map(([key]) => normalizeKey(key))
  const sanitized: Record<string, unknown> = {}
  for (const [key, value] of entries) {
    const normalized = normalizeKey(key)
    const masked =
      isSensitiveKey(key) ||
      (!isError && isPersonName(normalized, ctx.parentKey, keys))
    sanitized[sanitizeStringValue(key, ctx)] = masked
      ? value == null
        ? value
        : MASKED_VALUE
      : toSanitizedValue(value, descend(ctx, normalized))
  }
  return sanitized
}

function errorToObject(error: Error): Record<string, unknown> {
  return {
    ...error,
    name: error.name,
    message: error.message,
    stack: error.stack,
    ...(error.cause !== undefined && { cause: error.cause }),
  }
}

function sanitizeStringValue(str: string, ctx: Context): string {
  const trimmed = str.trim()
  if (
    (trimmed.startsWith('{') && trimmed.endsWith('}')) ||
    (trimmed.startsWith('[') && trimmed.endsWith(']'))
  ) {
    try {
      const parsed: unknown = JSON.parse(trimmed.replace(BIG_INT_RE, '"$&"'))
      return JSON.stringify(toSanitizedValue(parsed, ctx))
    } catch {
      // not JSON, fall through to pattern masking
    }
  }
  let masked = ctx.stripAnsi ? str.replace(ANSI_RE, '') : str
  if (FORM_BODY_RE.test(masked)) {
    masked = masked
      .split('&')
      .map((pair) => {
        const key = pair.slice(0, pair.indexOf('='))
        return maskPair(key, `${key}=${MASKED_VALUE}`, pair)
      })
      .join('&')
  }
  if (PATH_SEGMENT_RE) {
    masked = masked.replace(
      PATH_SEGMENT_RE,
      (match, parent: string, segment: string) =>
        PUBLIC_PATH_SEGMENTS.includes(segment.toLowerCase())
          ? match
          : `/${parent}/${MASKED_VALUE}`
    )
  }
  masked = masked.replace(KEY_VALUE_RE, (match, prefix: string, key: string) =>
    maskPair(key, `${prefix}${key}=${MASKED_VALUE}`, match)
  )
  if (HTTP_MESSAGE_RE.test(masked)) {
    masked = masked.replace(
      HEADER_LINE_RE,
      (match, prefix: string, key: string, separator: string) =>
        maskPair(key, `${prefix}${key}:${separator}${MASKED_VALUE}`, match)
    )
  }
  masked = masked
    .replace(TOKEN_VALUE_RE, `$1$2${MASKED_VALUE}`)
    .replace(JWT_RE, MASKED_VALUE)
    .replace(EMAIL_RE, MASKED_VALUE)
    .replace(PHONE_INTL_RE, MASKED_VALUE)
  return projectPatterns.reduce(
    (value, pattern) => value.replace(pattern, MASKED_VALUE),
    masked
  )
}
